import { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  type SuppPlannerPastRun,
  type SuppPlannerResult,
  type SuppPlannerScoredItem,
  type SuppPlannerSource,
  getSuppPlannerRun,
  importSuppPlannerToBuildQueue,
  listSuppPlannerRuns,
  runSuppPlannerResearch,
} from "../lib/api";
import { Button, Field, Select, TextInput } from "../components/Form";
import type { WorkspaceContext } from "./ClientWorkspacePage";

/**
 * Supporting Content Planner tab.
 *
 *   - User enters a service term.
 *   - We fetch PAA queries (stub) + Reddit question-form posts (real, free API).
 *   - Each candidate goes through Claude for scoring (0-100), title rewriting,
 *     and reasoning. Real API call, ~$0.05-0.15 per run.
 *   - Results show ranked, with checkboxes. User picks the best ones and
 *     sends them to Build Queue as supporting_content articles.
 *   - "Past runs" dropdown loads cached results per service term.
 */
export default function SuppPlannerTab() {
  const { client } = useOutletContext<WorkspaceContext>();
  const navigate = useNavigate();

  const [serviceTerm, setServiceTerm] = useState("");
  const [pastRuns, setPastRuns] = useState<SuppPlannerPastRun[]>([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SuppPlannerResult | null>(null);

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);

  // Load past runs on mount
  useEffect(() => {
    let cancelled = false;
    listSuppPlannerRuns(client.id)
      .then((data) => {
        if (cancelled) return;
        setPastRuns(data.runs);
      })
      .catch(console.error);
    return () => {
      cancelled = true;
    };
  }, [client.id]);

  async function handleRun() {
    if (!serviceTerm.trim()) {
      setError("Service term is required.");
      return;
    }
    setError(null);
    setRunning(true);
    setSelectedKeys(new Set());
    try {
      const data = await runSuppPlannerResearch(client.id, serviceTerm.trim());
      setResult(data.result);
      // Refresh past runs
      const runs = await listSuppPlannerRuns(client.id);
      setPastRuns(runs.runs);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Research failed.");
    } finally {
      setRunning(false);
    }
  }

  async function handleLoadPastRun(term: string) {
    if (!term) return;
    setError(null);
    setSelectedKeys(new Set());
    try {
      const data = await getSuppPlannerRun(client.id, term);
      if (data.result) {
        setResult(data.result);
        setServiceTerm(term);
      } else {
        setError("Past run not found — it may have been cleared.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load past run.");
    }
  }

  function toggleSelected(key: string) {
    const next = new Set(selectedKeys);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelectedKeys(next);
  }

  function toggleSelectAll() {
    if (!result) return;
    if (selectedKeys.size === result.items.length) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(new Set(result.items.map((it) => it.rawQuery)));
    }
  }

  async function handleSendToBuildQueue() {
    if (!result || selectedKeys.size === 0) return;
    setImporting(true);
    setError(null);
    try {
      const selected = result.items.filter((it) => selectedKeys.has(it.rawQuery));
      await importSuppPlannerToBuildQueue(
        client.id,
        selected.map((it) => ({
          title: it.title,
          rawQuery: it.rawQuery,
          source: it.source,
        }))
      );
      navigate(`/clients/${client.id}/content-generation`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import failed.");
      setImporting(false);
    }
  }

  const selectedCount = selectedKeys.size;
  const totalCount = result?.items.length ?? 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-bg-subtle border border-border flex items-center justify-center text-text-muted shrink-0">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 2 7 12 12 22 7 12 2" />
            <polyline points="2 17 12 22 22 17" />
            <polyline points="2 12 12 17 22 12" />
          </svg>
        </div>
        <div>
          <h1 className="text-text font-semibold text-xl">Supporting Pages</h1>
          <p className="text-text-muted text-sm mt-0.5">
            Discover real questions people ask, score them by SEO value, and push
            to content generation.
          </p>
        </div>
      </div>

      {/* Input card */}
      <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card">
        <h2 className="text-text font-semibold text-sm mb-3">
          Research a service term
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_240px] gap-3 items-end">
          <div>
            <label className="block text-xs text-text-muted font-medium mb-1.5">
              Service Term
            </label>
            <TextInput
              value={serviceTerm}
              onChange={setServiceTerm}
              placeholder="e.g. cosmetic dentistry"
            />
          </div>
          <div>
            <Button
              variant="primary"
              onClick={handleRun}
              disabled={running || !serviceTerm.trim()}
            >
              {running ? "↻ Researching..." : "🔍 Research"}
            </Button>
          </div>
          <div>
            <Select
              value=""
              onChange={(v) => handleLoadPastRun(v)}
              options={[
                { value: "", label: "Past runs..." },
                ...pastRuns.map((r) => ({
                  value: r.serviceTerm,
                  label: `${r.serviceTerm} (${r.candidateCount})`,
                })),
              ]}
            />
          </div>
        </div>

        {error && <p className="text-danger text-sm mt-3">{error}</p>}
      </div>

      {/* Research in progress */}
      {running && (
        <div className="bg-bg-card border border-border rounded-lg shadow-card overflow-hidden">
          <div className="px-5 py-3 border-b border-border-subtle">
            <h2 className="text-text font-semibold text-sm">
              Research in progress
            </h2>
          </div>
          <div className="px-5 py-10 text-center">
            <p className="text-text-muted text-sm flex items-center justify-center gap-2">
              <span className="animate-spin">↻</span>
              Researching questions via DataForSEO…
            </p>
          </div>
        </div>
      )}

      {/* Results */}
      {result && result.items.length > 0 && (
        <>
          <div>
            <div className="flex items-center justify-between">
              <h2 className="text-text font-semibold">
                Scored Questions{" "}
                <span className="text-text-muted text-sm font-normal">
                  {totalCount} questions ranked
                </span>
              </h2>
              <button
                type="button"
                onClick={toggleSelectAll}
                className="text-accent text-sm hover:underline"
              >
                {selectedCount === totalCount ? "Deselect All" : "Select All"} ·{" "}
                <span className="text-text-muted">{selectedCount} selected</span>
              </button>
            </div>
            {/* Diagnostic strip — shows where candidates came from. Helps
                users see at a glance whether a thin result is because PAA
                returned little, Reddit was empty, etc. Only renders if the
                cached/fresh result includes sourceCounts (older cached runs
                pre-dating fix21 don't have this field). */}
            {result.sourceCounts && (
              <p className="text-text-subtle text-xs mt-1 flex items-center gap-2 flex-wrap">
                <span>Sources:</span>
                <span className="text-accent">
                  PAA {result.sourceCounts.paa}
                </span>
                <span className="text-text-muted">·</span>
                <span className="text-accent">
                  Related {result.sourceCounts.related}
                </span>
                <span className="text-text-muted">·</span>
                <span className="text-text-muted">
                  Reddit {result.sourceCounts.reddit}
                </span>
                <span className="text-text-muted">·</span>
                <span className="text-text-muted">
                  Total {result.sourceCounts.total}
                </span>
              </p>
            )}
          </div>

          <div className="bg-bg-card border border-border rounded-lg shadow-card overflow-hidden">
            {result.items.map((item) => (
              <ScoredQuestionRow
                key={item.rawQuery}
                item={item}
                selected={selectedKeys.has(item.rawQuery)}
                onToggle={() => toggleSelected(item.rawQuery)}
              />
            ))}
          </div>

          <div className="bg-bg-card border border-border rounded-lg p-3 shadow-card flex items-center justify-between">
            <span className="text-text-muted text-sm">
              {selectedCount === 0
                ? "Select questions to send to content generation"
                : `${selectedCount} question${selectedCount === 1 ? "" : "s"} selected`}
            </span>
            <Button
              variant="primary"
              onClick={handleSendToBuildQueue}
              disabled={selectedCount === 0 || importing}
            >
              {importing ? "Importing..." : `→ Send to Build Queue${selectedCount > 0 ? ` (${selectedCount})` : ""}`}
            </Button>
          </div>
        </>
      )}

      {result && result.items.length === 0 && (
        <div className="bg-bg-card border border-border rounded-lg p-8 text-center shadow-card">
          <p className="text-text-muted text-sm">
            No question-form candidates found for "{result.serviceTerm}". Try a
            different service term.
          </p>
        </div>
      )}
    </div>
  );
}

// =========================================================================
// Scored question row
// =========================================================================
function ScoredQuestionRow({
  item,
  selected,
  onToggle,
}: {
  item: SuppPlannerScoredItem;
  selected: boolean;
  onToggle: () => void;
}) {
  const [showReasoning, setShowReasoning] = useState(false);

  return (
    <div className="border-b border-border-subtle last:border-b-0 px-3 py-2 hover:bg-bg-hover/40 flex items-start gap-2.5">
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        className="mt-1 accent-accent"
      />
      <ScoreBadge score={item.score} />
      <div className="flex-1 min-w-0">
        <h3 className="text-text text-sm font-medium leading-snug">
          {item.title}
        </h3>
        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
          <span className="text-text-muted text-xs leading-tight">
            {item.rawQuery}
          </span>
          <SourceBadge source={item.source} />
          {item.sourceUrl && (
            <a
              href={item.sourceUrl}
              target="_blank"
              rel="noreferrer"
              className="text-text-subtle hover:text-accent text-xs"
              title="View source"
            >
              ↗
            </a>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowReasoning(!showReasoning)}
          className="text-accent text-[11px] mt-1 hover:underline"
        >
          {showReasoning ? "Hide reasoning" : "Show reasoning"}
        </button>
        {showReasoning && (
          <p className="text-text-muted text-xs mt-1 leading-relaxed">
            {item.reasoning}
          </p>
        )}
      </div>
    </div>
  );
}

// =========================================================================
// Badges
// =========================================================================
function ScoreBadge({ score }: { score: number }) {
  // Color bands match the reference screenshot:
  //   80-100: green (high commercial value)
  //   70-79:  light green/yellow (good)
  //   60-69:  amber (moderate)
  //   50-59:  light amber (borderline)
  //   <50:    gray (low)
  let classes = "bg-bg-subtle text-text-muted border-border";
  if (score >= 80) {
    classes = "bg-success-bg text-success border-success-border";
  } else if (score >= 70) {
    classes = "bg-success-bg/60 text-success border-success-border";
  } else if (score >= 60) {
    classes = "bg-warning-bg text-warning border-warning-border";
  } else if (score >= 50) {
    classes = "bg-warning-bg/60 text-warning border-warning-border";
  }

  return (
    <span
      className={`text-xs font-bold px-1.5 py-0.5 rounded border w-8 text-center flex-shrink-0 mt-0.5 ${classes}`}
    >
      {score}
    </span>
  );
}

function SourceBadge({ source }: { source: SuppPlannerSource }) {
  // Color-coded by source so users can quickly distinguish provenance:
  //   PAA      = accent blue (canonical search-demand signal)
  //   Related  = blue (lighter — adjacent to PAA but lower confidence)
  //   Reddit   = orange (off-platform community signal)
  //   AI       = success green (synthesized topical-cluster idea)
  const map: Record<SuppPlannerSource, { bg: string; label: string }> = {
    paa: { bg: "bg-accent text-white", label: "PAA" },
    related: {
      bg: "bg-accent-subtle text-accent border border-accent/30",
      label: "Related",
    },
    reddit: { bg: "bg-reddit text-white", label: "Reddit" },
    ai: {
      bg: "bg-success-bg text-success border border-success-border",
      label: "AI",
    },
  };
  const cfg = map[source];
  return (
    <span
      className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${cfg.bg}`}
    >
      {cfg.label}
    </span>
  );
}
